/* quick-add-cart-sync.js */
/* Keeps quick-add highlights in sync with the cart:
   - On theme:cart:change, removes highlights/storage for variants no longer in the cart.
   - Robust against byId / byHandle differences.
*/

(() => {
  const DEBUG = false;
  const log = (...a) => DEBUG && console.log('[QA CartSync]', ...a);

  const STORAGE_KEY = 'qaSelectedVariantIds:v1';

  // ---- storage helpers (must match quick-add-variant-highlight.js format) ----
  function loadStore() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : { byId: {}, byHandle: {} };
    } catch {
      return { byId: {}, byHandle: {} };
    }
  }
  function saveStore(obj) {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(obj)); } catch {}
  }

  // Unpaint any visible button for a given variant id, and drop it from toolbar datasets
  function unpaintVariantEverywhere(variantId) {
    const vid = String(variantId);
    // Any button on the page that represents this variant
    document.querySelectorAll(`[data-add-to-cart][data-variant-id="${CSS.escape(vid)}"]`).forEach(btn => {
      // clear visual state
      btn.classList.remove('is-selected');
      btn.setAttribute('aria-pressed', 'false');
      // remove from its toolbar dataset if present
      const tb = btn.closest('[id^="quick-add-menu-toolbar"], [role="toolbar"]');
      if (tb) {
        const set = new Set(
          (tb.dataset.qaSelectedVariantIds || '')
            .split(',')
            .map(s => s.trim())
            .filter(Boolean)
        );
        if (set.delete(vid)) {
          tb.dataset.qaSelectedVariantIds = [...set].join(',');
        }
      }
    });
  }

  // Build Set of "productId:variantId" keys and Set of variantIds in cart
  function mapsFromCart(cart) {
    const combos = new Set();
    const variantIds = new Set();
    if (!cart || !Array.isArray(cart.items)) return { combos, variantIds };
    for (const it of cart.items) {
      const pid = String(it.product_id || it.product?.id || '');
      const vid = String(it.variant_id || it.id || '');
      if (pid && vid) combos.add(`${pid}:${vid}`);
      if (vid) variantIds.add(vid);
    }
    return { combos, variantIds };
  }

  // Hard sync: drop any stored variantId that is NOT in the cart anymore (byId and byHandle)
  function hardSyncStoreWithCart(cart) {
    const store = loadStore();
    const { variantIds: inCart } = mapsFromCart(cart);

    const removedVids = new Set();

    // prune byId
    for (const pid of Object.keys(store.byId)) {
      const before = store.byId[pid] || [];
      const after = before.filter(vid => inCart.has(String(vid)));
      if (after.length !== before.length) {
        // collect removed
        before.forEach(vid => { if (!inCart.has(String(vid))) removedVids.add(String(vid)); });
        store.byId[pid] = after;
      }
    }
    // prune byHandle
    for (const handle of Object.keys(store.byHandle)) {
      const before = store.byHandle[handle] || [];
      const after = before.filter(vid => inCart.has(String(vid)));
      if (after.length !== before.length) {
        before.forEach(vid => { if (!inCart.has(String(vid))) removedVids.add(String(vid)); });
        store.byHandle[handle] = after;
      }
    }

    // save once
    saveStore(store);

    // unpaint all removed variants everywhere
    removedVids.forEach(unpaintVariantEverywhere);

    if (DEBUG) log('hardSync removed', [...removedVids]);
  }

  // Track previous cart to detect removals quickly (optional optimization)
  let prevCombos = null;

  function initPrev() {
    if (prevCombos) return;
    try {
      fetch(`${window.theme?.routes?.cart || '/cart'}.js`)
        .then(r => r.ok ? r.json() : null)
        .then(cart => { prevCombos = mapsFromCart(cart).combos; })
        .catch(() => { prevCombos = new Set(); });
    } catch {
      prevCombos = new Set();
    }
  }

  // On cart change, do a hard sync (simple & reliable)
  function onCartChange(newCart) {
    hardSyncStoreWithCart(newCart);
    prevCombos = mapsFromCart(newCart).combos;
  }

  // Wire up
  initPrev();

  document.addEventListener('theme:cart:change', (ev) => {
    const cart = ev?.detail?.cart;
    if (!cart) return;
    onCartChange(cart);
  });

  document.addEventListener('theme:cart:init',   () => initPrev());
  document.addEventListener('theme:cart:reload', () => initPrev());
})();
